import { FloatingPortal, Placement, useId } from '@floating-ui/react';
import { CalendarSimpleOutlined } from '@icon';
import classNames from 'classnames';
import hoistNonReactStatics from 'hoist-non-react-statics';
import moment from 'moment';
import React from 'react';
import { useIntl } from 'react-intl';
import Form from '../../form';
import { TFormVariant } from '../../form/Form.types';
import CalendarPanel from '../components/calendar-panel';
import { TDatePanelOption } from '../components/calendar-panel/calendar-panel.types';
import CalendarPlaceholder from '../components/calendar-placeholder';
import { getDateFormat, TPanelMode } from '../date-time.utils';
import RangePickerDays from './components/range-picker-days';
import RangePickerMonths from './components/range-picker-months';
import RangePickerYears from './components/range-picker-years';
import { InputSC, InputSkeletonSC } from './range-picker.styled';
import { useFloatingPicker, useRangePickerCalendar } from './range-picker.utils';

type RangePickerProps = {
  value?: Date[];
  onChange?: (dates: Date[]) => void;
  presets?: TDatePanelOption[];
  locale?: Intl.LocalesArgument;
  minDate?: Date | undefined;
  maxDate?: Date | undefined;
  disabled?: boolean;
  error?: boolean;
  placement?: Placement;
  dateFormat?: string;
  clearable?: boolean;
  style?: React.CSSProperties;
  className?: string;
  variant?: TFormVariant;
  left?: React.ReactNode;
  right?: React.ReactNode;
  renderExtraFooter?: () => React.ReactNode;
  outlined?: boolean;
};

export interface RangePickerHandle {
  focus: (input?: 'start' | 'end') => void;
  blur: () => void;
}

const RangePicker = (props: RangePickerProps, forwardedRef?: React.Ref<RangePickerHandle>) => {
  const {
    value,
    onChange,
    locale,
    minDate,
    maxDate,
    placement = 'bottom-start',
    clearable = true,
    variant,
    style,
    className,
    disabled,
    error,
    presets,
    left,
    right,
    renderExtraFooter,
    outlined,
  } = props;
  const settings = Form.useFormSettings({ variant, disabled });
  const { locale: intlLocale } = useIntl();
  const mergedLocale = (locale ?? intlLocale) as string;
  const [panelMode, setPanelMode] = React.useState<TPanelMode>('date');
  const hasValue = value && value?.length > 0;
  const dateFormat = props.dateFormat ?? getDateFormat('date', mergedLocale);

  const { data, propGetters } = useRangePickerCalendar({
    locale: mergedLocale,
    maxDate,
    minDate,
    onChange,
    value,
  });

  const startRef = React.useRef<HTMLInputElement>(null);
  const endRef = React.useRef<HTMLInputElement>(null);
  const [startValue, setStartValue] = React.useState('');
  const [endValue, setEndValue] = React.useState('');

  const {
    floatingProps: { x, y, refs, strategy },
    interactionProps: { getFloatingProps, getReferenceProps },
    open,
    setOpen,
  } = useFloatingPicker({ placement });

  const headingId = useId();
  const referenceProps = getReferenceProps() as any;

  const handleStartChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const dateForm = moment(e.target.value, dateFormat, true);
    setStartValue(e.target.value);

    if (dateForm.isValid()) {
      const nextDate = dateForm.toDate();
      onChange?.(value?.[1] ? [nextDate, value[1]] : [nextDate]);
    }
  };
  const handleEndChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const dateTo = moment(e.target.value, dateFormat, true);
    setEndValue(e.target.value);

    if (dateTo.isValid()) {
      const nextDate = dateTo.toDate();

      onChange?.(
        value?.[0] && dateTo.isAfter(value[0]) ? [value[0], nextDate] : [nextDate, nextDate],
      );
    }
  };

  const resetInputValue = (setter: (n: string) => void, nextDate?: Date) => {
    if (nextDate) {
      setter(moment(nextDate).format(dateFormat));
    } else {
      setter('');
    }
  };

  React.useEffect(() => {
    resetInputValue(setStartValue, value?.[0]);
    resetInputValue(setEndValue, value?.[1]);
  }, [value]);

  React.useEffect(() => {
    if (!open) {
      startRef.current?.blur();
      endRef.current?.blur();
      setPanelMode('date');
    }
  }, [open]);

  const onClick = (e: React.MouseEvent<any>) => {
    e.preventDefault();
    e.stopPropagation();
    startRef.current?.focus();
  };

  React.useImperativeHandle(
    forwardedRef,
    () => ({
      blur: () => {
        setOpen(false);
        startRef.current?.blur();
        endRef.current?.blur();
      },
      focus: (input?: 'start' | 'end') => {
        (input === 'end' ? endRef.current : startRef.current)?.focus();
      },
    }),
    [],
  );

  return (
    <>
      <InputSkeletonSC
        outlined={outlined}
        style={style}
        className={classNames('ui-range-picker', className)}
        onClick={onClick}
        ref={refs.setReference}
        left={left}
        right={
          <InputSkeletonSC.ClearAction
            right={
              right !== undefined ? (
                right
              ) : (
                <InputSkeletonSC.Action
                  Icon={CalendarSimpleOutlined}
                  onClick={() => {
                    startRef.current?.focus();
                  }}
                  variant={settings.variant}
                />
              )
            }
            clearable={hasValue && clearable}
            onClearPress={() => {
              onChange?.([]);
            }}
          />
        }
        focused={open}
        error={error}
        disabled={settings.disabled}
        variant={settings.variant}
      >
        <span style={{ position: 'relative' }}>
          <InputSC
            onFocus={referenceProps.onFocus}
            ref={startRef}
            value={startValue}
            onChange={handleStartChange}
            onBlur={(e) => {
              resetInputValue(setStartValue, value?.[0]);
              referenceProps.onBlur(e);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                endRef.current?.focus();
              }
            }}
            disabled={settings.disabled}
          />
          <CalendarPlaceholder
            placeholder={dateFormat}
            value={startValue}
            variant={settings.variant}
          />
        </span>
        <span className="input-separator">-</span>
        <span style={{ position: 'relative' }}>
          <InputSC
            onFocus={referenceProps.onFocus}
            ref={endRef}
            value={endValue}
            onChange={handleEndChange}
            onClick={(e) => e.stopPropagation()}
            onBlur={(e) => {
              resetInputValue(setEndValue, value?.[1]);
              referenceProps.onBlur(e);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                startRef.current?.focus();
              }
            }}
            disabled={settings.disabled}
          />
          <CalendarPlaceholder
            placeholder={dateFormat}
            value={endValue}
            variant={settings.variant}
          />
        </span>
      </InputSkeletonSC>
      <FloatingPortal>
        {open ? (
          <CalendarPanel
            ref={refs.setFloating}
            style={{
              position: strategy,
              transform: `translate(${x ?? 0}px, ${y ?? 0}px)`,
              top: 0,
              left: 0,
              zIndex: 9999999,
            }}
            aria-labelledby={headingId}
            options={presets}
            onOptionClick={(_, sD) => {
              onChange?.(sD);
            }}
            {...getFloatingProps()}
            footer={renderExtraFooter?.()}
          >
            {panelMode === 'date' ? (
              <RangePickerDays
                setOpen={setOpen}
                setPanelMode={setPanelMode}
                calendars={data.calendars}
                propGetters={propGetters}
                weekDays={data.weekDays}
              />
            ) : null}
            {panelMode === 'month' ? (
              <RangePickerMonths
                setOpen={setOpen}
                setPanelMode={setPanelMode}
                onChange={onChange}
                mode={'date'}
                calendars={data.calendars}
                months={data.months}
                propGetters={propGetters}
              />
            ) : null}
            {panelMode === 'year' ? (
              <RangePickerYears
                setOpen={setOpen}
                setPanelMode={setPanelMode}
                onChange={onChange}
                mode={'date'}
                propGetters={propGetters}
                years={data.years}
              />
            ) : null}
          </CalendarPanel>
        ) : null}
      </FloatingPortal>
    </>
  );
};

RangePicker.Action = InputSkeletonSC.Action;
RangePicker.ClearAction = InputSkeletonSC.ClearAction;
RangePicker.Icon = InputSkeletonSC.Icon;

export default hoistNonReactStatics(React.forwardRef(RangePicker), RangePicker);
